import { ERROR_HANDLER } from '@/api';
import { errorHandlerService } from '@/api/error-handler';
import { PaymentError } from '@/interfaces';
import { useQuery } from '@tanstack/react-query';
import { usePathname } from 'next/navigation';

interface NotificationError {
	id: string;
	name: string;
	date: string;
}

export const useHandleError = () => {
	const pathname = usePathname();
	const { data: errors, isLoading: isLoadingErrors } = useQuery({
		queryKey: [ERROR_HANDLER],
		queryFn: () => errorHandlerService.getErrors(),
		staleTime: 5000, // 5 segundos que los datos se consideren "frescos"
		gcTime: 1000 * 60 * 5, // 5 minutos que se mantenga en cache,
		refetchOnWindowFocus: false,
		// refetchInterval: 10000,
		refetchInterval: (query) => {
			const data = query.state.data;
			// Solo hace polling si hay errores sin resolver
			return data?.data && data?.data.length > 0 ? 10000 : 30000;
		},
		enabled: pathname.includes('/lawyerapp'), // available to lawyerapp only
	});

	const marketingErrors =
		errors?.data.filter(({ entity_type }) => entity_type === 'marketing') ?? [];
	const casesErrors =
		errors?.data.filter(({ entity_type }) => entity_type === 'cases') ?? [];

	const hasCasesErrors = casesErrors.length > 0;
	const hasMarketingErrors = marketingErrors.length > 0;

	const scheduledErrors = [
		'META_INCORRECT_IMAGE_ASPECT_RATIO',
		'GOOGLE_ADS_INCORRECT_IMAGE_ASPECT_RATIO',
		'GOOGLE_ADS_CREDIT_CARD_NOT_FUNDS',
		'GOOGLE_ADS_FAILURE',
		'META_API_FAILURE',
		'META_CREDIT_CARD_NOT_FUNDS',
		'META_GEOGRAPHIC_OVERLAP',
		'META_LOW_AMOUNT_BUDGET',
	];

	const caseHasError = (id: string): boolean => {
		return casesErrors.some(({ entity_id }) => entity_id === id);
	};

	const campaignHasError = (id: string): boolean => {
		return marketingErrors.some(({ entity_id }) => entity_id === id);
	};

	const campaignCaseHasError = (id: string): boolean => {
		return marketingErrors.some(({ input_data }) => input_data?.case_id === id);
	};

	const getErrorsByCampaign = (id: string): PaymentError[] => {
		return marketingErrors.filter(({ entity_id }) => entity_id === id);
	};

	const getErrorsByCase = (id: string): PaymentError[] => {
		return casesErrors.filter(({ entity_id }) => entity_id === id);
	};

	const hasCharacterLimitError = (id: string, tab: string): boolean => {
		const errors = getErrorsByCampaign(id as string);

		const errorsByTab = errors.filter(
			(item) => item.input_data?.type.toLowerCase() === tab
		);

		return errorsByTab.some(
			(item) => item.type_error === 'GOOGLE_ADS_CHARACTER_LIMIT_EXCEEDED'
		);
	};

	const hasImageAspectRatioError = (id: string, tab: string): boolean => {
		const errors = getErrorsByCampaign(id as string);

		const errorsByTab = errors.filter(
			(item) => item.input_data?.type.toLowerCase() === tab
		);

		return errorsByTab.some(
			(item) =>
				item.type_error === 'META_INCORRECT_IMAGE_ASPECT_RATIO' ||
				item.type_error === 'GOOGLE_ADS_INCORRECT_IMAGE_ASPECT_RATIO'
		);
	};

	const hasScheduleError = (id: string): boolean => {
		const errors = getErrorsByCampaign(id as string);
		return errors.some((item) => scheduledErrors.includes(item.type_error));
	};

	const getCasesNotificationError = () => {
		const uniqueCases: NotificationError[] = [];
		casesErrors.forEach((c) => {
			const exist = uniqueCases.some((uc) => uc.id === c.entity_id);
			if (!exist) {
				uniqueCases.push({
					id: c.entity_id,
					name: c.case_name,
					date: c.created_at,
				});
			}
		});

		return uniqueCases;
	};

	const getMarketingNotificationError = () => {
		const uniqueCampaigns: NotificationError[] = [];
		marketingErrors.forEach((c) => {
			const exist = uniqueCampaigns.some((uc) => uc.id === c.entity_id);
			if (!exist) {
				uniqueCampaigns.push({
					id: c.entity_id,
					name: c.case_name,
					date: c.created_at,
				});
			}
		});

		return uniqueCampaigns;
	};

	return {
		marketingErrors,
		casesErrors,
		isLoadingErrors,
		hasCasesErrors,
		hasMarketingErrors,
		caseHasError,
		campaignHasError,
		campaignCaseHasError,
		getErrorsByCase,
		getErrorsByCampaign,
		hasCharacterLimitError,
		hasImageAspectRatioError,
		hasScheduleError,
		getCasesNotificationError,
		getMarketingNotificationError,
	};
};
